// Edit one SAM rulebook rule in plain language: name, conditions ("when"), and what the screen does
// ("then"). Pickers are built from the backend's editor_schema — the editor never offers a fact,
// action, option or card the server would reject.
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RulebookSchema } from '@/redux/internal-services/platformAdminApi';
import {
  defaultValueFor, operatorsFor, rowProblems, rowsToWhen, slugify, valuesFor, whenToRows,
  type ConditionRow, type Decision, type Operator, type Rule, type Toast,
} from './rulebookModel';

const field = 'w-full rounded-lg border border-ui-border bg-ui-card px-3 py-2 text-sm text-ui-text focus:outline-none focus:ring-2 focus:ring-brand-dark/30 min-h-11';
const label = 'block text-xs font-medium text-ui-text2 mb-1';

export const useRulebookLabels = () => {
  const { t } = useTranslation();
  return useMemo(() => ({
    t,
    fact: (n: string) => t(`rulebook.facts.${n}`, { defaultValue: n }),
    factHelp: (n: string) => t(`rulebook.factHelp.${n}`, { defaultValue: '' }),
    value: (v: string) => t(`rulebook.values.${v}`, { defaultValue: v }),
    op: (o: string) => t(`rulebook.ops.${o}`, { defaultValue: o }),
    action: (a: string) => t(`rulebook.actions.${a}`, { defaultValue: a }),
    actionHelp: (a: string) => t(`rulebook.actionHelp.${a}`, { defaultValue: '' }),
    option: (o: string) => t(`rulebook.options.${o}`, { defaultValue: o }),
    optionHelp: (o: string) => t(`rulebook.optionHelp.${o}`, { defaultValue: '' }),
    card: (c: string) => t(`rulebook.cards.${c}`, { defaultValue: c }),
    level: (l: string) => t(`rulebook.levels.${l}`, { defaultValue: l }),
    message: (code: number) => t(`messages.${code}`, { entity: t('rulebook.editor.sampleEntity'), detail: t('rulebook.editor.sampleDetail'), defaultValue: String(code) }),
  }), [t]);
};

// ── Small pickers ────────────────────────────────────────────────────────────

const MultiPick: React.FC<{ options: string[]; value: string[]; onChange: (v: string[]) => void; labelOf: (v: string) => string; id: string }> = ({ options, value, onChange, labelOf, id }) => (
  <div id={id} className="flex flex-wrap gap-1.5">
    {options.map((o) => {
      const on = value.includes(o);
      return (
        <button
          key={o}
          type="button"
          aria-pressed={on}
          onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])}
          className={`rounded-full border px-2.5 py-1 text-xs min-h-8 ${on ? 'border-brand-dark bg-brand-dark text-white' : 'border-ui-border bg-ui-card text-ui-text2 hover:border-brand-dark/50'}`}
        >
          {labelOf(o)}
        </button>
      );
    })}
  </div>
);

const ToastEditor: React.FC<{ schema: RulebookSchema; value: Toast; onChange: (v: Toast) => void; idPrefix: string }> = ({ schema, value, onChange, idPrefix }) => {
  const L = useRulebookLabels();
  const { min, max } = schema.toast.duration_ms;
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <div>
        <label className={label} htmlFor={`${idPrefix}-level`}>{L.t('rulebook.editor.toastLevel')}</label>
        <select id={`${idPrefix}-level`} className={field} value={value.level} onChange={(e) => onChange({ ...value, level: e.target.value })}>
          {schema.toast.levels.map((l) => <option key={l} value={l}>{L.level(l)}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className={label} htmlFor={`${idPrefix}-msg`}>{L.t('rulebook.editor.toastMessage')}</label>
        <select id={`${idPrefix}-msg`} className={field} value={value.message_code} onChange={(e) => onChange({ ...value, message_code: Number(e.target.value) })}>
          {schema.toast.message_codes.map((c) => <option key={c} value={c}>{L.message(c)}</option>)}
        </select>
      </div>
      <div className="sm:col-span-3">
        <label className={label} htmlFor={`${idPrefix}-dur`}>
          {L.t('rulebook.editor.toastDuration', { seconds: ((value.duration_ms ?? schema.toast.duration_ms.default) / 1000).toFixed(1) })}
        </label>
        <input
          id={`${idPrefix}-dur`} type="range" min={min} max={max} step={500} className="w-full accent-brand-dark"
          value={value.duration_ms ?? schema.toast.duration_ms.default}
          onChange={(e) => onChange({ ...value, duration_ms: Number(e.target.value) })}
        />
      </div>
    </div>
  );
};

// ── Condition row ────────────────────────────────────────────────────────────

const ConditionEditor: React.FC<{
  schema: RulebookSchema; row: ConditionRow; index: number; usedFacts: string[];
  onChange: (r: ConditionRow) => void; onRemove: () => void;
}> = ({ schema, row, index, usedFacts, onChange, onRemove }) => {
  const L = useRulebookLabels();
  const fact = schema.facts.find((f) => f.name === row.fact);
  const ops = operatorsFor(fact, schema.operators);
  const values = valuesFor(fact, schema);
  const labelOf = fact?.kind === 'card' || fact?.kind === 'cards' ? L.card : L.value;
  const id = `cond-${index}`;

  const setFact = (name: string) => {
    const f = schema.facts.find((x) => x.name === name);
    const op = operatorsFor(f, schema.operators)[0] ?? 'equals';
    onChange({ fact: name, op, value: defaultValueFor(f, op, schema) });
  };
  const setOp = (op: Operator) => onChange({ ...row, op, value: defaultValueFor(fact, op, schema) });

  let valueInput: React.ReactNode;
  if (row.op === 'exists' || fact?.kind === 'bool') {
    const yes = row.op === 'exists' ? 'rulebook.editor.hasValue' : 'rulebook.editor.yes';
    const no = row.op === 'exists' ? 'rulebook.editor.hasNoValue' : 'rulebook.editor.no';
    valueInput = (
      <select id={`${id}-val`} className={field} value={row.value ? 'true' : 'false'} onChange={(e) => onChange({ ...row, value: e.target.value === 'true' })}>
        <option value="true">{L.t(yes)}</option>
        <option value="false">{L.t(no)}</option>
      </select>
    );
  } else if (row.op === 'any_of' || row.op === 'not_in' || row.op === 'contains') {
    valueInput = <MultiPick id={`${id}-val`} options={values} value={(row.value as string[]) ?? []} onChange={(v) => onChange({ ...row, value: v })} labelOf={labelOf} />;
  } else if (fact?.kind === 'int') {
    valueInput = <input id={`${id}-val`} type="number" min={0} className={field} value={Number(row.value) || 0} onChange={(e) => onChange({ ...row, value: Number(e.target.value) })} />;
  } else if (values.length) {
    valueInput = (
      <select id={`${id}-val`} className={field} value={String(row.value ?? '')} onChange={(e) => onChange({ ...row, value: e.target.value })}>
        {values.map((v) => <option key={v} value={v}>{labelOf(v)}</option>)}
      </select>
    );
  } else {
    valueInput = <input id={`${id}-val`} className={field} value={String(row.value ?? '')} onChange={(e) => onChange({ ...row, value: e.target.value })} />;
  }

  return (
    <div className="rounded-xl border border-ui-border bg-ui-subtle p-3 grid gap-2">
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,12rem)_auto] items-end">
        <div className="min-w-0">
          <label className={label} htmlFor={`${id}-fact`}>{L.t('rulebook.editor.fact')}</label>
          <select id={`${id}-fact`} className={field} value={row.fact} onChange={(e) => setFact(e.target.value)}>
            <option value="" disabled>{L.t('rulebook.editor.pickFact')}</option>
            {schema.facts.map((f) => (
              <option key={f.name} value={f.name} disabled={f.name !== row.fact && usedFacts.includes(f.name)}>{L.fact(f.name)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={label} htmlFor={`${id}-op`}>{L.t('rulebook.editor.operator')}</label>
          <select id={`${id}-op`} className={field} value={row.op} onChange={(e) => setOp(e.target.value as Operator)}>
            {ops.map((o) => <option key={o} value={o}>{L.op(o)}</option>)}
          </select>
        </div>
        <button type="button" onClick={onRemove} className="min-h-11 rounded-lg px-3 text-sm text-danger hover:bg-red-50">
          {L.t('rulebook.editor.remove')}
        </button>
      </div>
      {row.fact && L.factHelp(row.fact) && <p className="text-xs text-ui-text3">{L.factHelp(row.fact)}</p>}
      <div className="min-w-0">{valueInput}</div>
    </div>
  );
};

// ── The dialog ───────────────────────────────────────────────────────────────

export interface RuleEditorProps {
  schema: RulebookSchema;
  rule: Rule;
  isNew: boolean;
  takenIds: string[];
  onCancel: () => void;
  onApply: (rule: Rule) => void;
}

const RuleEditor: React.FC<RuleEditorProps> = ({ schema, rule, isNew, takenIds, onCancel, onApply }) => {
  const L = useRulebookLabels();
  const [ruleId, setRuleId] = useState(rule.rule_id);
  const [description, setDescription] = useState(rule.description ?? '');
  const [enabled, setEnabled] = useState(rule.enabled !== false);
  const [rows, setRows] = useState<ConditionRow[]>(() => whenToRows(rule.when));
  const [decision, setDecision] = useState<Decision>(rule.decision);
  const [advanced, setAdvanced] = useState<Record<string, string>>({});

  const options = schema.options.filter((o) => o.actions === null || o.actions.includes(decision.action));
  const multiMode = (decision.multi_create as string) ?? 'LAST_ONLY';
  const defaultToast = (): Toast => ({ type: 'TOAST', level: schema.toast.levels.includes('SUCCESS') ? 'SUCCESS' : schema.toast.levels[0], message_code: schema.toast.message_codes[0], duration_ms: schema.toast.duration_ms.default });

  const setAction = (action: string) => {
    // Drop options that are not valid with the new action — the server would reject them.
    const next: Decision = { action };
    for (const o of schema.options) {
      if (o.name in decision && (o.actions === null || o.actions.includes(action))) next[o.name] = decision[o.name];
    }
    setDecision(next);
  };
  const setOpt = (name: string, value: unknown) => {
    const next = { ...decision };
    if (value === undefined) delete next[name]; else next[name] = value;
    setDecision(next);
  };
  const setMulti = (mode: string) => {
    const next: Decision = { ...decision, multi_create: mode };
    if (mode === 'LAST_ONLY') delete next.multi_create_feedback;
    else if (!next.multi_create_feedback) next.multi_create_feedback = defaultToast();
    setDecision(next);
  };

  const idTaken = takenIds.includes(ruleId);
  const problems = [
    ...(!ruleId ? ['missing_id'] : []),
    ...(idTaken ? ['duplicate_id'] : []),
    ...rowProblems(rows),
    ...Object.values(advanced).some((txt) => { try { JSON.parse(txt); return false; } catch { return true; } }) ? ['bad_json'] : [],
  ];

  const apply = () => {
    const d: Decision = { ...decision };
    for (const [name, txt] of Object.entries(advanced)) d[name] = JSON.parse(txt);
    onApply({ ...rule, rule_id: ruleId, description: description || undefined, enabled, when: rowsToWhen(rows), decision: d });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="rule-editor-title">
      <div className="bg-ui-card w-full sm:max-w-2xl max-h-[92vh] flex flex-col rounded-t-2xl sm:rounded-2xl shadow-xl">
        <div className="flex items-start justify-between gap-3 p-4 sm:p-5 border-b border-ui-border">
          <div className="min-w-0">
            <h2 id="rule-editor-title" className="text-base font-semibold text-ui-text">
              {isNew ? L.t('rulebook.editor.newTitle') : L.t('rulebook.editor.editTitle')}
            </h2>
            <p className="text-xs text-ui-text3 mt-0.5">{L.t('rulebook.editor.subtitle')}</p>
          </div>
          <button type="button" onClick={onCancel} className="min-h-11 min-w-11 rounded-lg text-ui-text2 hover:bg-ui-hover" aria-label={L.t('rulebook.editor.close')}>✕</button>
        </div>

        <div className="overflow-y-auto p-4 sm:p-5 grid gap-6">
          {/* Name */}
          <section className="grid gap-3">
            <div>
              <label className={label} htmlFor="rule-id">{L.t('rulebook.editor.name')}</label>
              <input id="rule-id" className={field} value={ruleId} onChange={(e) => setRuleId(slugify(e.target.value))} placeholder={L.t('rulebook.editor.namePlaceholder')} />
              {idTaken && <p className="text-xs text-danger mt-1">{L.t('rulebook.editor.problems.duplicate_id')}</p>}
            </div>
            <div>
              <label className={label} htmlFor="rule-desc">{L.t('rulebook.editor.description')}</label>
              <textarea id="rule-desc" rows={2} className={field} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={L.t('rulebook.editor.descriptionPlaceholder')} />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-ui-text min-h-11">
              <input id="rule-enabled" type="checkbox" className="h-4 w-4 accent-brand-dark" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              {L.t('rulebook.editor.enabled')}
            </label>
          </section>

          {/* When */}
          <section className="grid gap-2">
            <h3 className="text-sm font-semibold text-ui-text">{L.t('rulebook.editor.whenTitle')}</h3>
            <p className="text-xs text-ui-text3">{rows.length ? L.t('rulebook.editor.whenHelp') : L.t('rulebook.editor.whenEmpty')}</p>
            {rows.map((r, i) => (
              <ConditionEditor
                key={i} schema={schema} row={r} index={i}
                usedFacts={rows.map((x) => x.fact)}
                onChange={(nr) => setRows(rows.map((x, j) => (j === i ? nr : x)))}
                onRemove={() => setRows(rows.filter((_, j) => j !== i))}
              />
            ))}
            <button type="button" onClick={() => setRows([...rows, { fact: '', op: 'equals', value: '' }])} className="justify-self-start min-h-11 rounded-lg border border-dashed border-brand-dark/50 px-3 text-sm text-brand-dark hover:bg-brand-light/40">
              + {L.t('rulebook.editor.addCondition')}
            </button>
          </section>

          {/* Then */}
          <section className="grid gap-3">
            <h3 className="text-sm font-semibold text-ui-text">{L.t('rulebook.editor.thenTitle')}</h3>
            <div>
              <label className={label} htmlFor="rule-action">{L.t('rulebook.editor.action')}</label>
              <select id="rule-action" className={field} value={decision.action} onChange={(e) => setAction(e.target.value)}>
                {schema.actions.map((a) => <option key={a} value={a}>{L.action(a)}</option>)}
              </select>
              {L.actionHelp(decision.action) && <p className="text-xs text-ui-text3 mt-1">{L.actionHelp(decision.action)}</p>}
            </div>

            {options.map((o) => {
              const id = `opt-${o.name}`;
              if (o.name === 'multi_create_feedback') return null; // edited together with multi_create
              if (o.name === 'multi_create') {
                return (
                  <fieldset key={o.name} className="rounded-xl border border-ui-border p-3 grid gap-2">
                    <legend className="px-1 text-xs font-medium text-ui-text2">{L.option(o.name)}</legend>
                    <p className="text-xs text-ui-text3">{L.optionHelp(o.name)}</p>
                    {(o.values ?? []).map((m) => (
                      <label key={m} className={`flex items-start gap-2 rounded-lg p-2 cursor-pointer ${multiMode === m ? 'bg-brand-light/40' : 'hover:bg-ui-hover'}`}>
                        <input type="radio" name="multi_create" className="mt-1 accent-brand-dark" checked={multiMode === m} onChange={() => setMulti(m)} />
                        <span className="min-w-0">
                          <span className="block text-sm text-ui-text">{L.t(`rulebook.multi.${m}`, { defaultValue: m })}</span>
                          <span className="block text-xs text-ui-text3">{L.t(`rulebook.multiHelp.${m}`, { defaultValue: '' })}</span>
                        </span>
                      </label>
                    ))}
                    {multiMode !== 'LAST_ONLY' && (
                      <div className="grid gap-2 pt-1">
                        <p className="text-xs font-medium text-ui-text2">{L.option('multi_create_feedback')}</p>
                        <ToastEditor schema={schema} idPrefix="mc-toast" value={(decision.multi_create_feedback as Toast) ?? defaultToast()} onChange={(v) => setOpt('multi_create_feedback', v)} />
                      </div>
                    )}
                  </fieldset>
                );
              }
              if (o.kind === 'toast') {
                const on = !!decision[o.name];
                return (
                  <fieldset key={o.name} className="rounded-xl border border-ui-border p-3 grid gap-2">
                    <label className="inline-flex items-center gap-2 text-sm text-ui-text min-h-11">
                      <input id={id} type="checkbox" className="h-4 w-4 accent-brand-dark" checked={on} onChange={(e) => setOpt(o.name, e.target.checked ? defaultToast() : undefined)} />
                      {L.option(o.name)}
                    </label>
                    {L.optionHelp(o.name) && <p className="text-xs text-ui-text3">{L.optionHelp(o.name)}</p>}
                    {on && <ToastEditor schema={schema} idPrefix={id} value={decision[o.name] as Toast} onChange={(v) => setOpt(o.name, v)} />}
                  </fieldset>
                );
              }
              if (o.kind === 'bool') {
                return (
                  <div key={o.name}>
                    <label className="inline-flex items-center gap-2 text-sm text-ui-text min-h-11">
                      <input id={id} type="checkbox" className="h-4 w-4 accent-brand-dark" checked={!!(decision[o.name] ?? o.default)} onChange={(e) => setOpt(o.name, e.target.checked === o.default ? undefined : e.target.checked)} />
                      {L.option(o.name)}
                    </label>
                    {L.optionHelp(o.name) && <p className="text-xs text-ui-text3 -mt-1 ml-6">{L.optionHelp(o.name)}</p>}
                  </div>
                );
              }
              if (o.kind === 'cards') {
                const cur = decision[o.name];
                const list = cur == null ? [] : Array.isArray(cur) ? (cur as string[]) : [String(cur)];
                return (
                  <div key={o.name}>
                    <label className={label} htmlFor={id}>{L.option(o.name)}</label>
                    {L.optionHelp(o.name) && <p className="text-xs text-ui-text3 mb-1.5">{L.optionHelp(o.name)}</p>}
                    <MultiPick id={id} options={schema.cards} value={list} labelOf={L.card} onChange={(v) => {
                      const next = { ...decision };
                      if (!v.length) { delete next[o.name]; delete next.strict_prefer; } else next[o.name] = v.length === 1 ? v[0] : v;
                      setDecision(next);
                    }} />
                  </div>
                );
              }
              if (o.kind === 'enum') {
                return (
                  <div key={o.name}>
                    <label className={label} htmlFor={id}>{L.option(o.name)}</label>
                    <select id={id} className={field} value={String(decision[o.name] ?? '')} onChange={(e) => setOpt(o.name, e.target.value || undefined)}>
                      <option value="">{L.t('rulebook.editor.defaultOption', { value: L.value(String(o.default)) })}</option>
                      {(o.values ?? []).map((v) => <option key={v} value={v}>{L.value(v)}</option>)}
                    </select>
                    {L.optionHelp(o.name) && <p className="text-xs text-ui-text3 mt-1">{L.optionHelp(o.name)}</p>}
                  </div>
                );
              }
              // object (e.g. overlay settings): advanced, shown as text
              const txt = advanced[o.name] ?? (decision[o.name] != null ? JSON.stringify(decision[o.name], null, 2) : '');
              if (!txt && !(o.name in decision)) return null;
              return (
                <details key={o.name} className="rounded-xl border border-ui-border p-3">
                  <summary className="cursor-pointer text-sm text-ui-text2">{L.option(o.name)} · {L.t('rulebook.editor.advanced')}</summary>
                  <textarea id={id} rows={4} className={`${field} font-mono text-xs mt-2`} value={txt} onChange={(e) => setAdvanced({ ...advanced, [o.name]: e.target.value })} />
                </details>
              );
            })}
          </section>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 p-4 sm:p-5 border-t border-ui-border">
          <div className="text-xs text-danger min-w-0">
            {problems.map((p) => <div key={p}>{L.t(`rulebook.editor.problems.${p}`)}</div>)}
          </div>
          <div className="flex gap-2 ml-auto">
            <button type="button" onClick={onCancel} className="min-h-11 rounded-lg px-4 text-sm text-ui-text2 hover:bg-ui-hover">{L.t('rulebook.editor.cancel')}</button>
            <button type="button" disabled={problems.length > 0} onClick={apply} className="min-h-11 rounded-lg bg-brand-dark px-4 text-sm font-medium text-white disabled:opacity-40">
              {L.t('rulebook.editor.apply')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RuleEditor;
